import React, { useState } from 'react';
import ActivityResources from './ActivityResources';
import VideoThumbnail from './VideoThumbnail';
import { Icons } from '../lib/Icons';

/**
 * Modal to display detailed information about a KB activity/tile
 * Shows how-to steps, watch-outs, and resources (buy + DIY)
 */
const TileModal = ({ activity, tile, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('explanation'); // 'explanation' | 'howto' | 'diy' | 'videos' | 'buy' | 'resources'
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  
  // Support both new activity format and legacy tile format
  const item = activity || (tile?.items?.[0]);
  
  if (!isOpen || !item) return null;

  const formatFrequency = (freq) => {
    if (!freq) return 'As needed';
    
    const { perWeek, sessionMinutes } = freq;
    
    if (perWeek === 5) {
      return `Daily, ${sessionMinutes} minutes`;
    } else if (perWeek >= 3) {
      return `${perWeek}× per week, ${sessionMinutes} minutes`;
    } else if (perWeek === 2) {
      return `Twice weekly, ${sessionMinutes} minutes`;
    } else {
      return `Weekly, ${sessionMinutes} minutes`;
    }
  };

  // Get primary video from various sources
  const getPrimaryVideo = () => {
    // First check new resource system (array format)
    if (Array.isArray(item.resources)) {
      const videoResource = item.resources.find(r => r.kind === 'video' && r.status?.isActive !== false);
      if (videoResource) {
        return {
          url: videoResource.baseUrl,
          title: videoResource.title,
          thumbnail: videoResource.meta?.thumbnail,
          channel: videoResource.meta?.channel,
          videoId: extractYouTubeVideoId(videoResource.baseUrl),
        };
      }
    }
    
    // Check legacy video_url
    if (item.video_url) {
      return {
        url: item.video_url,
        title: 'Tutorial Video',
        videoId: extractYouTubeVideoId(item.video_url),
      };
    }
    
    // Check legacy resources.videos array
    if (item.resources?.videos && item.resources.videos.length > 0) {
      const video = item.resources.videos[0];
      return {
        url: video.url,
        title: video.title || 'Tutorial Video',
        videoId: extractYouTubeVideoId(video.url),
      };
    }
    
    return null;
  };

  // Extract YouTube video ID from URL
  const extractYouTubeVideoId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) return match[1];
    }
    return null;
  };

  const primaryVideo = getPrimaryVideo();

  // Determine title - support both formats
  const title = item.title || item.name || item.label || 'Activity';
  const description = item.description || item.what || '';

  return (
    <div 
      className="fixed inset-0 bg-black/30 flex justify-center items-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-4 right-4 text-gray-500 hover:text-black text-2xl font-bold z-10"
          onClick={onClose}
        >
          ×
        </button>
        
        <div className="p-6">
          {/* Title */}
          <h2 className="text-2xl font-bold mb-4">{title}</h2>
          
          {/* Metadata badges */}
          <div className="flex flex-wrap gap-2 mb-6">
            {item.targetDomains?.map((domain, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
              >
                <Icons.Target className="w-3.5 h-3.5" />
                {domain}
              </span>
            ))}
            {item.recommendedFrequency && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                <Icons.Clock className="w-3.5 h-3.5" />
                {formatFrequency(item.recommendedFrequency)}
              </span>
            )}
          </div>

          {/* Featured Video - Quick Access */}
          {primaryVideo && (
            <div className="mb-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-100 overflow-hidden">
              {showVideoPlayer && primaryVideo.videoId ? (
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${primaryVideo.videoId}?autoplay=1`}
                    title={primaryVideo.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="p-4 flex items-center gap-4">
                  <div 
                    className="relative cursor-pointer group flex-shrink-0"
                    onClick={() => setShowVideoPlayer(true)}
                  >
                    {(primaryVideo.thumbnail || primaryVideo.videoId) ? (
                      <VideoThumbnail
                        src={primaryVideo.thumbnail}
                        videoId={primaryVideo.videoId}
                        alt={primaryVideo.title}
                        className="w-40 h-24 object-cover rounded-lg"
                      />
                    ) : (
                      <div className="w-40 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                        <Icons.Film className="w-10 h-10 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 rounded-lg group-hover:bg-opacity-50 transition-all">
                      <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="text-white text-xl ml-1">▶</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-red-600 font-semibold uppercase tracking-wide mb-1">
                      Featured Video
                    </div>
                    <h4 className="font-semibold text-gray-900 line-clamp-2">
                      {primaryVideo.title}
                    </h4>
                    {primaryVideo.channel && (
                      <p className="text-sm text-gray-600">{primaryVideo.channel}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setShowVideoPlayer(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex-shrink-0"
                  >
                    ▶ Watch Now
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-4">
              <button
                onClick={() => setActiveTab('explanation')}
                className={`py-2 px-3 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                  activeTab === 'explanation'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icons.BookOpen className="w-4 h-4" />
                Explanation
              </button>
              <button
                onClick={() => setActiveTab('howto')}
                className={`py-2 px-3 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                  activeTab === 'howto'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icons.ClipboardDocumentList className="w-4 h-4" />
                How To
              </button>
              <button
                onClick={() => setActiveTab('diy')}
                className={`py-2 px-3 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                  activeTab === 'diy'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icons.Cog6Tooth className="w-4 h-4" />
                DIY
              </button>
              <button
                onClick={() => setActiveTab('videos')}
                className={`py-2 px-3 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                  activeTab === 'videos'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icons.Film className="w-4 h-4" />
                Videos
              </button>
              <button
                onClick={() => setActiveTab('buy')}
                className={`py-2 px-3 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                  activeTab === 'buy'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icons.ArrowTopRightOnSquare className="w-4 h-4" />
                Buy
              </button>
              {item.resources && Array.isArray(item.resources) && item.resources.length > 0 && (
                <button
                  onClick={() => setActiveTab('resources')}
                  className={`py-2 px-3 border-b-2 font-medium text-sm flex items-center gap-1.5 ${
                    activeTab === 'resources'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icons.FolderOpen className="w-4 h-4" />
                  Resources
                </button>
              )}
            </nav>
          </div>
          
          {/* Tab Content */}
          <div className="min-h-[300px]">
            {/* Tab 1: Explanation */}
            {activeTab === 'explanation' && (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">What is this activity?</h3>
                  <p className="text-gray-700 leading-relaxed">{description}</p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Why is it important?</h3>
                  <p className="text-gray-700 leading-relaxed">
                    This activity supports development in: {item.targetDomains?.join(', ')}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Recommended Frequency</h3>
                  <p className="text-gray-700">{formatFrequency(item.recommendedFrequency)}</p>
                </div>
              </div>
            )}

            {/* Tab 2: How To */}
            {activeTab === 'howto' && (
              <div className="space-y-6">
                {item.howTo && item.howTo.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Step-by-Step Instructions</h3>
                    <ol className="list-decimal list-inside text-gray-700 space-y-3">
                      {item.howTo.map((step, index) => (
                        <li key={index} className="pl-2 leading-relaxed">{step}</li>
                      ))}
                    </ol>
                  </div>
                )}
                
                {item.watchOutFor && item.watchOutFor.length > 0 && (
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <Icons.ExclamationTriangle className="w-5 h-5 text-amber-500 mr-2" /> What to Watch For
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-2">
                      {item.watchOutFor.map((warning, index) => (
                        <li key={index} className="pl-2">{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Tab 3: DIY Resources */}
            {activeTab === 'diy' && (
              <div>
                {item.resources?.diy && item.resources.diy.length > 0 ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">DIY Options (Free/Low-Cost)</h3>
                    {item.resources.diy.map((resource, index) => (
                      <div key={index} className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <h4 className="font-semibold text-gray-900 mb-3">{resource.name}</h4>
                        <ol className="list-decimal list-inside text-gray-700 space-y-2">
                          {resource.steps.map((step, stepIndex) => (
                            <li key={stepIndex} className="pl-2 leading-relaxed">{step}</li>
                          ))}
                        </ol>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No DIY resources available for this activity.</p>
                  </div>
                )}
              </div>
            )}

            {/* Tab 4: Video Resources */}
            {activeTab === 'videos' && (
              <div>
                {item.video_url || item.resources?.videos ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Video Tutorials</h3>
                    
                    {item.video_url && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <a
                          href={item.video_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                        >
                          <Icons.Film className="w-4 h-4" />
                          <span>Watch Tutorial Video</span>
                        </a>
                      </div>
                    )}
                    
                    {item.resources?.videos && item.resources.videos.map((video, index) => (
                      <div key={index} className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-2">{video.title}</h4>
                        <a
                          href={video.url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-600 hover:text-blue-800 underline flex items-center gap-2"
                        >
                          <Icons.Film className="w-4 h-4" />
                          <span>Watch on YouTube</span>
                        </a>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No video resources available for this activity.</p>
                  </div>
                )}
              </div>
            )}

            {/* Tab 5: Purchasable Resources - external links hidden until we curate them better */}
            {activeTab === 'buy' && (
              <div className="text-center py-8 text-gray-500">
                <p>External product links are temporarily unavailable. We are reviewing how to surface these better.</p>
              </div>
            )}

            {/* Tab 6: New Resources System */}
            {activeTab === 'resources' && (
              <div>
                <ActivityResources activity={item} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TileModal;
